import { type FC } from 'react';
import { BaseEdge, type EdgeProps } from '@xyflow/react';

/**
 * Edge that routes around the Default Actions node via a right-side bypass,
 * then merges back to center above the End node.
 *
 * 5-segment path with 4 rounded corners:
 *   source → down to channelY → right to outerX → down to mergeY → left to targetX → down to target.
 *
 * All edges share outerX/mergeY/targetX so they visually merge into one collector line to End.
 */
export const BypassEdge: FC<EdgeProps> = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  markerEnd,
  data,
}) => {
  const edgeData = data as Record<string, unknown> | undefined;
  const outerX = edgeData?.outerX as number | undefined;
  const channelY = edgeData?.channelY as number | undefined;
  const mergeY = edgeData?.mergeY as number | undefined;
  const r = 10;

  if (outerX == null || channelY == null || mergeY == null) {
    return (
      <BaseEdge
        path={`M ${sourceX} ${sourceY} L ${targetX} ${targetY}`}
        style={style}
        markerEnd={markerEnd}
      />
    );
  }

  const dirH1 = outerX >= sourceX ? 1 : -1;
  const dirH2 = targetX <= outerX ? -1 : 1;

  const segV1 = Math.abs(channelY - sourceY);
  const segH1 = Math.abs(outerX - sourceX);
  const segV2 = Math.abs(mergeY - channelY);
  const segH2 = Math.abs(targetX - outerX);
  const segV3 = Math.abs(targetY - mergeY);

  const cr1 = Math.max(0, Math.min(r, segV1 / 2, segH1 / 2));
  const cr2 = Math.max(0, Math.min(r, segH1 / 2, segV2 / 2));
  const cr3 = Math.max(0, Math.min(r, segV2 / 2, segH2 / 2));
  const cr4 = Math.max(0, Math.min(r, segH2 / 2, segV3 / 2));

  const path = [
    `M ${sourceX} ${sourceY}`,
    // Down to channel (above DA)
    `L ${sourceX} ${channelY - cr1}`,
    `Q ${sourceX} ${channelY} ${sourceX + dirH1 * cr1} ${channelY}`,
    // Right to outerX
    `L ${outerX - dirH1 * cr2} ${channelY}`,
    `Q ${outerX} ${channelY} ${outerX} ${channelY + cr2}`,
    // Down past DA to mergeY
    `L ${outerX} ${mergeY - cr3}`,
    `Q ${outerX} ${mergeY} ${outerX + dirH2 * cr3} ${mergeY}`,
    // Left to center (merge with DA→End vertical)
    `L ${targetX - dirH2 * cr4} ${mergeY}`,
    `Q ${targetX} ${mergeY} ${targetX} ${mergeY + cr4}`,
    // Down to End (collector section, overlaps DA→End)
    `L ${targetX} ${targetY}`,
  ].join(' ');

  return <BaseEdge path={path} style={style} markerEnd={markerEnd} />;
};
